import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Autocomplete,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import PrintService from '../../../service/PrintTemplate';
import { Reportsv2 } from '../../../service/Reports';
import { PrintTemplateType } from '../../../types/print-template/types';
import {
  CamposOcultosImpressao,
  ChecklistReportType,
  ConfiguracoesImpressao,
  OtimizacaoHtml,
} from '../../../types/reports/types';
import { useMutation, useQuery } from '@tanstack/react-query';

type Props = {
  open: boolean;
  onClose: () => void;
  reports: ChecklistReportType[];
  initialTemplate?: PrintTemplateType | null;
};

type ApiPrintTemplate = PrintTemplateType & {
  defaultTemplate?: boolean;
};

type PrintConfiguration = {
  showLegend?: boolean;
  showTemplateLogo?: boolean;
  showTemplateHeader?: boolean;
  showTemplateFooter?: boolean;
  showCompanyName?: boolean;
  showHierarchy?: boolean;
  showIdentifier?: boolean;
  showEquipe?: boolean;
  showUsuario?: boolean;
  showDates?: boolean;
  showScoring?: boolean;
  showRange?: boolean;
  showApproval?: boolean;
  showComments?: boolean;
  showImages?: boolean;
  showFiles?: boolean;
  showSubitemType?: boolean;
  showApprovers?: boolean;
  showHostDetails?: boolean;
  showUnansweredOptions?: boolean;
  showNotApplicable?: boolean;
  showDuplicates?: boolean;
  ticketDisplay?: 'hide' | 'some' | 'all';
};

const printTemplateService = new PrintService();
const reportsService = new Reportsv2();

type SharePdfResponse = {
  success?: boolean;
  data?: {
    downloadUrl?: string;
    cached?: boolean;
    dataInicioNaoEncontradas?: number[];
  };
};

type PrintHtmlRequest = {
  dataInicio: number[];
  camposOcultos: CamposOcultosImpressao;
  configuracoesImpressao: ConfiguracoesImpressao;
  otimizacaoHtml: OtimizacaoHtml;
};

type VisibilityKey =
  | 'showLegend'
  | 'showTemplateLogo'
  | 'showTemplateHeader'
  | 'showTemplateFooter'
  | 'showCompanyName'
  | 'showHierarchy'
  | 'showIdentifier'
  | 'showEquipe'
  | 'showUsuario'
  | 'showDates'
  | 'showScoring'
  | 'showRange'
  | 'showApproval'
  | 'showComments'
  | 'showImages'
  | 'showFiles'
  | 'showSubitemType'
  | 'showApprovers'
  | 'showHostDetails'
  | 'showUnansweredOptions'
  | 'showNotApplicable'
  | 'showDuplicates';

const visibilityOptions: Array<{ key: VisibilityKey; label: string }> = [
  { key: 'showLegend', label: 'Legenda de conformidade' },
  { key: 'showTemplateLogo', label: 'Logo do template' },
  { key: 'showTemplateHeader', label: 'Cabeçalho do template' },
  { key: 'showTemplateFooter', label: 'Rodapé do template' },
  { key: 'showCompanyName', label: 'Nome da empresa' },
  { key: 'showHierarchy', label: 'Hierarquia (cliente, local, conjunto e unidade)' },
  { key: 'showIdentifier', label: 'Identificador' },
  { key: 'showEquipe', label: 'Equipes' },
  { key: 'showUsuario', label: 'Usuário executor' },
  { key: 'showDates', label: 'Datas de início e fim' },
  { key: 'showScoring', label: 'Scoring' },
  { key: 'showRange', label: 'Range' },
  { key: 'showApproval', label: 'Status de aprovação' },
  { key: 'showComments', label: 'Comentários' },
  { key: 'showImages', label: 'Referências de imagens e assinaturas' },
  { key: 'showFiles', label: 'Arquivos anexos' },
  { key: 'showSubitemType', label: 'Tipo de cada subitem' },
  { key: 'showApprovers', label: 'Aprovadores' },
  { key: 'showHostDetails', label: 'Detalhes do hospedeiro' },
  { key: 'showUnansweredOptions', label: 'Opções não selecionadas' },
  { key: 'showNotApplicable', label: 'Itens não aplicáveis' },
  { key: 'showDuplicates', label: 'Chamados duplicados' },
];

const defaultPrintConfiguration: PrintConfiguration = {
  showLegend: true,
  showTemplateLogo: true,
  showTemplateHeader: true,
  showTemplateFooter: true,
  showCompanyName: true,
  showHierarchy: true,
  showIdentifier: true,
  showEquipe: true,
  showUsuario: true,
  showDates: true,
  showScoring: true,
  showRange: true,
  showApproval: true,
  showComments: true,
  showImages: true,
  showFiles: true,
  showSubitemType: true,
  showApprovers: true,
  showHostDetails: true,
  showUnansweredOptions: false,
  showNotApplicable: true,
  showDuplicates: true,
  ticketDisplay: 'all',
};

export default function ModalShareReports({ open, onClose, reports, initialTemplate }: Props) {
  const [templates, setTemplates] = useState<ApiPrintTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [sharedDownloadUrl, setSharedDownloadUrl] = useState<string | null>(null);
  const [printConfiguration, setPrintConfiguration] = useState<PrintConfiguration>(defaultPrintConfiguration);
  const [showPrintOptions, setShowPrintOptions] = useState(true);
  const pdfUrlRef = useRef<string | null>(null);

  const reportCountLabel = useMemo(() => `${reports.length} ${reports.length === 1 ? 'relatório selecionado' : 'relatórios selecionados'}`, [reports.length]);
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );
  const printRequest = useMemo(() => {
    const dataInicio = reports.map((report) => Number(report.inicio));
    const camposOcultos: CamposOcultosImpressao = {
      showHeaders: true,
      hideHierarchy: {
        cliente: !(printConfiguration.showHierarchy ?? true),
        local: !(printConfiguration.showHierarchy ?? true),
        conjunto: !(printConfiguration.showHierarchy ?? true),
        unidade: !(printConfiguration.showHierarchy ?? true),
      },
      hideEquipe: !(printConfiguration.showEquipe ?? true),
      hideUser: !(printConfiguration.showUsuario ?? true),
      hideIdent: !(printConfiguration.showIdentifier ?? true),
      hideAprov: !(printConfiguration.showApproval ?? true),
      hideDate: {
        inicio: !(printConfiguration.showDates ?? true),
        fim: !(printConfiguration.showDates ?? true),
      },
      hideNAplica: !(printConfiguration.showNotApplicable ?? true),
      hideImages: !(printConfiguration.showImages ?? true),
      hideFiles: !(printConfiguration.showFiles ?? true),
      radioOption: printConfiguration.ticketDisplay ?? 'all',
      showNotSelected: printConfiguration.showUnansweredOptions ?? false,
      showScoring: printConfiguration.showScoring ?? true,
      showRange: printConfiguration.showRange ?? true,
      showDuplicatas: printConfiguration.showDuplicates ?? true,
      ocultaNomeEmpresa: !(printConfiguration.showCompanyName ?? true),
      exibeTipo: printConfiguration.showSubitemType ?? true,
      exibeDetalheHospedeiro: printConfiguration.showHostDetails ?? true,
    };
    const configuracoesImpressao: ConfiguracoesImpressao = {
      printTemplate: selectedTemplate ? {
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        dafaultTemplate: selectedTemplate.dafaultTemplate,
        cliente: selectedTemplate.cliente,
        empresa: selectedTemplate.empresa,
        headerText: selectedTemplate.headerText,
        footerText: selectedTemplate.footerText,
        color: selectedTemplate.color,
        logo: selectedTemplate.logo,
        status: selectedTemplate.status,
      } : undefined,
    };

    const otimizacaoHtml: OtimizacaoHtml = {
      minify: true,
      brotli: true,
    };

    return { dataInicio, camposOcultos, configuracoesImpressao, otimizacaoHtml };
  }, [printConfiguration, reports, selectedTemplate]);

  const { isLoading, isFetching } = useQuery<ApiPrintTemplate[]>({
    queryKey: ['GET_PRINT_TEMPLATES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        const response = await printTemplateService.getAllTemplates(token);
        const printTemplates: ApiPrintTemplate[] = Array.isArray(response?.printTemplates)
          ? response.printTemplates as ApiPrintTemplate[]
          : [];
        const activeTemplates = printTemplates.filter((template) => template.status === true);
        setTemplates(activeTemplates);
        const templateFromDetailModal = initialTemplate?.id
          ? activeTemplates.find((template) => template.id === initialTemplate.id)
          : undefined;
        const defaultTemplate = activeTemplates.find((template) => Boolean(template.dafaultTemplate));
        setSelectedTemplateId(templateFromDetailModal?.id ?? defaultTemplate?.id ?? activeTemplates[0]?.id ?? null);
        return activeTemplates;
      } catch (err) {
        console.error(err);
        throw new Error('Não foi possível carregar os templates de impressão.');
      }
    },
    retry: 0,
    enabled: open,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const onLoadingTemplates = isLoading || isFetching;

  const { mutateAsync: requestSharedPdf } = useMutation<SharePdfResponse, Error, PrintHtmlRequest>({
    mutationKey: ['GET_SHARED_REPORT_PDF'],
    retry: 0,
    mutationFn: async (request) => {
      if (request.dataInicio.some((inicio) => !Number.isSafeInteger(inicio) || inicio <= 0)) {
        throw new Error('Há um relatório selecionado sem uma data de início válida.');
      }

      const token = await AttToken();
      if (!token) throw new Error('Token não encontrado');

      return reportsService.getSharePdf(
        token,
        request.dataInicio,
        request.camposOcultos,
        request.configuracoesImpressao,
        request.otimizacaoHtml,
      ) as Promise<SharePdfResponse>;
    },
  });

  useEffect(() => {
    if (!open) return;
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = null;
    setDownloadUrl(null);
    setSharedDownloadUrl(null);
    setPrintConfiguration(defaultPrintConfiguration);
    setShowPrintOptions(true);
  }, [initialTemplate?.id, open]);

  useEffect(() => () => {
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
  }, []);

  const generatePdf = async () => {
    if (!reports.length) {
      toast.error('Nenhum relatório foi selecionado para compartilhamento.');
      return;
    }

    setGenerating(true);
    try {
      const result = await requestSharedPdf(printRequest);
      const sharedPdfUrl = result?.data?.downloadUrl;

      if (!result?.success || !sharedPdfUrl) {
        throw new Error('A API não retornou o link do PDF compartilhado.');
      }
      const pdfResponse = await fetch(sharedPdfUrl);
      if (!pdfResponse.ok) throw new Error('Não foi possível baixar o PDF compartilhado.');
      const blob = await pdfResponse.blob();
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      const shortDownloadUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = shortDownloadUrl;
      setDownloadUrl(shortDownloadUrl);
      setSharedDownloadUrl(sharedPdfUrl);
      setShowPrintOptions(false);
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível gerar o PDF para compartilhamento.');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = () => {
    if (!downloadUrl) return;
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'relatorios-axyma.pdf';
    anchor.click();
  };

  const shareDownloadLink = async () => {
    if (!sharedDownloadUrl) return;

    const shareData = {
      title: 'Relatórios Axyma',
      text: reportCountLabel,
      url: sharedDownloadUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(sharedDownloadUrl);
      toast.success('Link temporário copiado para a área de transferência.');
    } catch (error) {
      if ((error as DOMException).name !== 'AbortError') {
        toast.error('Não foi possível compartilhar o link temporário.');
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Compartilhar relatórios
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>{reportCountLabel}</Typography>
          <Autocomplete
            options={templates}
            value={selectedTemplate}
            loading={onLoadingTemplates}
            disabled={onLoadingTemplates || !templates.length || generating}
            getOptionLabel={(template) => template.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setSelectedTemplateId(value?.id ?? null)}
            renderInput={(params) => <TextField {...params} label="Template de impressão" />}
          />
          <Button
            variant="outlined"
            size="small"
            endIcon={showPrintOptions ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setShowPrintOptions((current) => !current)}>
            {showPrintOptions ? 'Ocultar opções do PDF' : 'Exibir opções do PDF'}
          </Button>
          {showPrintOptions && (
            <>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>Informações incluídas no PDF</Typography>
              <FormGroup row>
                {visibilityOptions.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        disabled={generating || Boolean(downloadUrl)}
                        checked={Boolean(printConfiguration[key])}
                        onChange={(_event, checked) => setPrintConfiguration((current: PrintConfiguration) => ({ ...current, [key]: checked }))}
                      />
                    }
                    label={label}
                    sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      disabled={generating || Boolean(downloadUrl)}
                      checked={printConfiguration.ticketDisplay !== 'hide'}
                      onChange={(_event, checked) => setPrintConfiguration((current: PrintConfiguration) => ({ ...current, ticketDisplay: checked ? 'all' : 'hide' }))}
                    />
                  }
                  label="Chamados"
                  sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                />
              </FormGroup>
            </>
          )}
          {!templates.length && !onLoadingTemplates && <Alert severity="info">Nenhum template de impressão ativo foi encontrado. O PDF será gerado sem template.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={onLoadingTemplates} onClick={onClose}>Cancelar</Button>
        {downloadUrl && <Button startIcon={<DownloadIcon />} onClick={downloadPdf}>Baixar PDF</Button>}
        {sharedDownloadUrl && <Button startIcon={<ShareIcon />} onClick={() => void shareDownloadLink()}>Compartilhar link</Button>}
        <Button variant="contained" disabled={!reports.length || generating || onLoadingTemplates} onClick={() => void generatePdf()}>
          {generating ? 'Gerando PDF...' : downloadUrl ? 'Gerar novamente' : 'Gerar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
